import React, { useState, useContext, useEffect } from 'react'
import GoBack from './src/GoBack'
import { Typography, Icon } from 'foundations-library/components'
import { makeStyles } from '@material-ui/core/styles'
import { getVerbalColors } from 'helpers'
import { UserContext } from 'providers'
import API from 'users-app/src/util/api_v5'
import ToggleItem from './src/ToggleItem'

const useStyles = makeStyles(theme => {
    const { colors } = theme['foundations-library']
    const verbalColors = getVerbalColors(colors)

    return {
        root: {

        },
        contentArea: {
            padding: 40
        },
        content: {
            display: 'flex',
            maxWidth: '900px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            margin: 'auto'
        },
        headerContainer: {
            display: 'flex',
            paddingTop: '10px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            alignSelf: 'stretch'
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
        },
        infoContainer: {
            display: 'flex',
            padding: '6px 10px',
            alignItems: 'center',
            gap: '6px',
            alignSelf: 'stretch',
            borderRadius: '5px',
            background: `linear-gradient(78deg, ${verbalColors['primary-100']} 9.2%, ${verbalColors['primary-50']} 48.86%, ${verbalColors['primary-100']} 91.7%)`
        },
        infoTipContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '4px',
            alignSelf: 'stretch',
            marginBottom: '8px'
        }
    }
})

/**
 *
 */
export enum Plan {
    pro = 'pro',
    plus = 'plus',
    basic = 'basic'
}
/**
 *
 */
export const CallSummary = {
    title: 'AI Call Summaries',
    subTitle: 'Enable AI Call Summaries that summarizes your calls into Action Items, Key Points and more.',
    type: 'call_summaries'
}
/**
 *
 */
export const EmailNotifications = {
    title: 'AI Summaries notifications',
    subTitle: 'Receive email notifications for the AI summaries',
    type: 'email_notifications'
}
/**
 *
 */
export const infoTips = {
    plusBasicAdmin: 'To enable AI summaries please upgrade to a Pro plan.',
    plusBasicNonAdmin: 'To enable AI summaries please reach out to your administrator.',
    proNonAdmin: 'AI Summaries are managed by your account administrator. Please reach out to them if you’d like to make any changes.'
}

/**
 *
 */
const Index = () => {
    const classes = useStyles()
    const userInfo = useContext(UserContext)
    const [isLoading, setIsLoading] = useState(true)
    const [aiSummaryPreferences, setAiSummaryPreferences] = useState<Record<string, any> | null>(null)

    const [isAISummaryEnabled, setIsAISummaryEnabled] = useState(false)
    const [isEmailNotificationsEnabled, setIsEmailNotificationsEnabled] = useState(false)

    const isAdmin = userInfo?.role === 'account'
    const plan = userInfo?.user_account_type.toLowerCase() as Plan

    useEffect(() => {
        const callApi = async () => {
            try {
                const response = await API.getUserCallSummaryPreferences(userInfo.extension_id)
                setAiSummaryPreferences(response?.data)
            } catch (error) {
                console.error('Failed to fetch call summary preferences:', error)
            } finally {
                setIsLoading(false)
            }
        }
        callApi()
    }, [])

    const renderTitle = () => (
        <div className={classes.titleContainer}>
            <Icon name='ai-solid' color='neutral-900' size={20} />
            <Typography variant='subtitle1' color='neutral-900'>AI Settings</Typography>
        </div>
    )

    const proNonAdmin = plan === Plan.pro && !isAdmin
    const plusBasicAdmin = plan !== Plan.pro && isAdmin
    const plusBasicNonAdmin = plan !== Plan.pro && !isAdmin

    const getAlertText = () => {
        if (plusBasicAdmin) return infoTips.plusBasicAdmin // plus/basic admin
        if (plusBasicNonAdmin || (proNonAdmin && !isAISummaryEnabled)) return infoTips.plusBasicNonAdmin // pro/plus/basic non admin
        return '' // pro admin
    }

    const renderAlert = () => {
        const alert = getAlertText()

        if (!alert) return null

        return (
            <div className={classes.infoContainer}>
                <Icon name='infotip-sm' color='neutral-900' size={24} />
                <Typography variant='subtitle2' color='neutral-900'>
                    {alert}
                </Typography>
            </div>
        )
    }

    const renderInfoTip = () => {
        if (!(proNonAdmin && isAISummaryEnabled)) return null

        return (
            <div className={classes.infoTipContainer}>
                <Icon name='infotip-sm' color='neutral-700' size='small' />
                <Typography variant='body3' color='neutral-700'>{infoTips.proNonAdmin}</Typography>
            </div>
        )
    }

    const disableCallSummary = plusBasicAdmin || plusBasicNonAdmin || proNonAdmin
    const disableEmailNotifications = plusBasicAdmin || plusBasicNonAdmin || (proNonAdmin && !isAISummaryEnabled)

    return (
        <div className={classes.root}>
            <GoBack title='AI Summaries' />
            <div className={classes.contentArea}>
                <div className={classes.content}>
                    <div className={classes.headerContainer}>
                        {renderTitle()}
                        {renderAlert()}
                    </div>
                    <ToggleItem {...CallSummary} loadingTheState={isLoading} enabled={Boolean(aiSummaryPreferences?.call_summaries)} switchEnabled={isAISummaryEnabled} setSwitchEnabled={setIsAISummaryEnabled} isOtherToggleEnabled={isEmailNotificationsEnabled} setOtherToggleEnabled={setIsEmailNotificationsEnabled} disabled={disableCallSummary} />
                    {renderInfoTip()}
                    <ToggleItem {...EmailNotifications} loadingTheState={isLoading} enabled={Boolean(aiSummaryPreferences?.email_notifications)} switchEnabled={isEmailNotificationsEnabled} setSwitchEnabled={setIsEmailNotificationsEnabled} isOtherToggleEnabled={isAISummaryEnabled} setOtherToggleEnabled={setIsAISummaryEnabled} disabled={disableEmailNotifications} />
                </div>
            </div>
        </div>
    )
}

Index.displayName = 'ai-summary'

export default Index
